import { useEffect, useMemo, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Copy, MoreVertical, Play, Plus, Search, SlidersHorizontal } from "lucide-react"
import { toast } from "sonner"
import { ScenarioCard } from "@/components/domain/scenario-card"
import { Dialog, DialogBody, DialogFooter, DialogHeader } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { SharedProps } from "@/types/inertia"
import type { TestLabBatchSummary, TestLabScenario, TestRunStatus } from "@/types/test_lab"

type Filter = "all" | "passing" | "failing" | "errored" | "never_run" | "skipped" | "review"

const SCENARIO_PAGE_SIZE = 10

type Props = {
  scenarios: TestLabScenario[]
  selectedId: number | null
  onSelect: (id: number) => void
  onRunOne: (scenarioId: number) => void
  onRunAll: () => void
  busyScenarioIds: Set<number>     // scenarios whose run start is in flight (UI-only optimistic state)
  runAllBusy: boolean
  // Skill id is needed to wire the "+ Add scenario" POST. Optional so
  // any consumer that hasn't passed it yet falls back gracefully (the
  // button just disables itself).
  skillId?: number
  latestBatch?: TestLabBatchSummary | null
  title?: string
  description?: string
  emptyMessage?: string
}

export function ScenarioList({
  scenarios,
  selectedId,
  onSelect,
  onRunOne,
  onRunAll,
  busyScenarioIds,
  runAllBusy,
  skillId,
  latestBatch = null,
  title = "Test scenarios",
  description = "Review scenario outcomes.",
  emptyMessage = "No scenarios for this skill yet.",
}: Props) {
  const { props } = usePage<SharedProps>()
  const isAdmin = props.isAdmin === true
  const [filter, setFilter] = useState<Filter>("all")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [addOpen, setAddOpen] = useState(false)

  const counts = useMemo(() => ({
    all: scenarios.length,
    passing: scenarios.filter((s) => s.latestRunStatus === "passed").length,
    failing: scenarios.filter((s) => s.latestRunStatus === "failed").length,
    errored: scenarios.filter((s) => s.latestRunStatus === "errored").length,
    never_run: scenarios.filter((s) => s.latestRunId == null).length,
    skipped: scenarios.filter((s) => s.latestRunStatus === "skipped").length,
  }), [scenarios])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return scenarios.filter((s) => {
      if (!matchesFilter(s.latestRunStatus, s.latestRunId, filter)) return false
      if (!q) return true
      return (
        (s.name ?? "").toLowerCase().includes(q) ||
        s.key.toLowerCase().includes(q) ||
        (s.goal ?? "").toLowerCase().includes(q)
      )
    })
  }, [scenarios, filter, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / SCENARIO_PAGE_SIZE))
  const currentPage = Math.min(Math.max(page, 1), totalPages)
  const pageStartIndex = (currentPage - 1) * SCENARIO_PAGE_SIZE
  const visibleScenarios = useMemo(
    () => filtered.slice(pageStartIndex, pageStartIndex + SCENARIO_PAGE_SIZE),
    [filtered, pageStartIndex],
  )
  const pageNumbers = useMemo(() => visiblePageNumbers(currentPage, totalPages), [currentPage, totalPages])
  const pageStart = filtered.length === 0 ? 0 : pageStartIndex + 1
  const pageEnd = Math.min(pageStartIndex + SCENARIO_PAGE_SIZE, filtered.length)
  const isFiltered = filter !== "all" || search.trim().length > 0
  const scenarioCountLabel =
    filtered.length === 0
      ? `0 of ${scenarios.length} scenarios`
      : isFiltered
        ? `${pageStart}-${pageEnd} of ${filtered.length} matches (${scenarios.length} total)`
        : `${pageStart}-${pageEnd} of ${scenarios.length} scenarios`

  useEffect(() => {
    setPage(1)
  }, [filter, search])

  useEffect(() => {
    setPage((current) => Math.min(Math.max(current, 1), totalPages))
  }, [totalPages])

  return (
    <div className="valet-panel flex h-full min-h-[520px] flex-col overflow-hidden rounded-card lg:min-h-0">
      <div className="border-b border-border p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <p className="mt-1 text-sm text-app-muted">{description}</p>
            {isAdmin && latestBatch && <LastBatchMeta batch={latestBatch} showCost={isAdmin} />}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              disabled={skillId == null}
              className="grid h-9 w-9 place-items-center rounded-control border border-border bg-white/[0.04] text-app-muted transition hover:bg-white/[0.07] hover:text-white disabled:opacity-50"
              title={skillId == null ? "Manual add is only available in a skill Test Lab for now." : "Manually add a scenario"}
            >
              <Plus size={15} />
            </button>
            <button
              type="button"
              onClick={onRunAll}
              disabled={runAllBusy || scenarios.length === 0}
              className="grid h-9 w-9 place-items-center rounded-control border border-brand-redBorder bg-brand-redSoft text-brand-red transition hover:bg-brand-redSoft/80 disabled:opacity-50"
              title={runAllBusy ? "Starting tests" : "Run all tests"}
            >
              <Play size={12} fill="currentColor" />
            </button>
            <button
              type="button"
              className="grid h-9 w-9 place-items-center rounded-control border border-border bg-white/[0.04] text-app-muted transition hover:bg-white/[0.07] hover:text-white"
              aria-label="Scenario menu"
            >
              <MoreVertical size={15} />
            </button>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <div className="relative min-w-0 flex-1">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-subtle" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search scenarios..."
              className="h-11 w-full rounded-control border border-border bg-black/25 pl-9 pr-3 text-sm text-white outline-none placeholder:text-app-subtle focus:border-brand-redBorder"
            />
          </div>
          <button
            type="button"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-control border border-border bg-white/[0.04] text-app-muted transition hover:bg-white/[0.07] hover:text-white"
            aria-label="Filter scenarios"
          >
            <SlidersHorizontal size={16} />
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <FilterPill active={filter === "all"} label="All" count={counts.all} onClick={() => setFilter("all")} tone="red" />
          <FilterPill active={filter === "failing"} label="Failed" count={counts.failing} onClick={() => setFilter("failing")} tone="red" />
          <FilterPill active={filter === "passing"} label="Passed" count={counts.passing} onClick={() => setFilter("passing")} tone="green" />
          <FilterPill active={filter === "review"} label="Needs review" count={counts.errored + counts.skipped} onClick={() => setFilter("review")} tone="amber" />
          {counts.never_run > 0 && (
            <FilterPill active={filter === "never_run"} label="Not run" count={counts.never_run} onClick={() => setFilter("never_run")} tone="neutral" />
          )}
        </div>
      </div>

      <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-app-muted">
            {scenarios.length === 0
              ? emptyMessage
              : "No matches for the current filter."}
          </div>
        ) : (
          visibleScenarios.map((s) => (
            <ScenarioCard
              key={s.id}
              scenario={s}
              selected={s.id === selectedId}
              onSelect={() => onSelect(s.id)}
              onRun={() => onRunOne(s.id)}
              busy={busyScenarioIds.has(s.id)}
            />
          ))
        )}
      </div>

      <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-app-muted">
        <span>{scenarioCountLabel}</span>
        <div className="flex items-center gap-1" aria-label="Scenario pages">
          {pageNumbers.map((pageNumber) => {
            const isCurrent = pageNumber === currentPage
            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
                disabled={isCurrent}
                aria-current={isCurrent ? "page" : undefined}
                aria-label={`Page ${pageNumber}`}
                className={cn(
                  "grid h-8 w-8 place-items-center rounded-control transition-colors",
                  isCurrent
                    ? "cursor-default bg-white/[0.06] text-white"
                    : "text-app-muted hover:bg-white/[0.04] hover:text-white",
                )}
              >
                {pageNumber}
              </button>
            )
          })}
        </div>
      </div>

      {skillId != null && (
        <AddScenarioDialog open={addOpen} onClose={() => setAddOpen(false)} skillId={skillId} />
      )}
    </div>
  )
}

function LastBatchMeta({ batch, showCost }: { batch: TestLabBatchSummary; showCost: boolean }) {
  const ranAt = batch.startedAt ? fmtCompactRelative(batch.startedAt) : "time unknown"
  const costUsd = typeof batch.costUsd === "number" && Number.isFinite(batch.costUsd) ? batch.costUsd : null
  const pieces = [
    `${batch.total} run${batch.total === 1 ? "" : "s"}`,
    `${batch.passed} passed`,
    `${batch.failed} failed`,
    `${batch.errored} errored`,
  ]
  if (batch.running > 0) pieces.push(`${batch.running} running`)

  function copyBatchId() {
    if (!navigator.clipboard) {
      toast.error("Clipboard is not available")
      return
    }

    void navigator.clipboard.writeText(String(batch.id)).then(
      () => toast.success(`Copied batch #${batch.id}`),
      () => toast.error("Could not copy")
    )
  }

  return (
    <div className="mt-2 flex max-w-full flex-wrap items-center gap-1.5 text-[11px] text-app-muted">
      <button
        type="button"
        onClick={copyBatchId}
        title="Copy latest batch ID"
        className="inline-flex min-h-5 items-center gap-1 rounded bg-white/5 px-1.5 py-0.5 text-app-muted transition hover:bg-white/10 hover:text-white"
      >
        <code>last batch #{batch.id}</code>
        <Copy size={10} aria-hidden="true" />
      </button>
      <span>{ranAt}</span>
      <span className="text-app-subtle">·</span>
      {showCost && costUsd != null && (
        <>
          <span>${costUsd.toFixed(4)}</span>
          <span className="text-app-subtle">·</span>
        </>
      )}
      <span>{pieces.join(" / ")}</span>
    </div>
  )
}

function FilterPill({
  active,
  label,
  count,
  tone,
  onClick,
}: {
  active: boolean
  label: string
  count: number
  tone: "red" | "green" | "amber" | "neutral"
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-chip border px-3 py-1.5 text-xs transition",
        active && tone === "red" && "border-brand-redBorder bg-brand-redSoft font-semibold text-brand-red",
        active && tone === "green" && "border-status-success/40 bg-status-success/10 font-semibold text-status-success",
        active && tone === "amber" && "border-status-warning/40 bg-status-warning/10 font-semibold text-status-warning",
        active && tone === "neutral" && "border-border-strong bg-white/[0.08] font-semibold text-white",
        !active && "border-border bg-white/[0.04] text-app-muted hover:bg-white/[0.07] hover:text-white",
      )}
    >
      {label} <span className="ml-1">{count}</span>
    </button>
  )
}

// Manual create dialog. Forge's draft_red_team_suite is the AI-assisted
// path — this is the bare-hands version when the operator already knows
// the scenario they want.
function AddScenarioDialog({ open, onClose, skillId }: { open: boolean; onClose: () => void; skillId: number }) {
  const [name, setName]       = useState("")
  const [persona, setPersona] = useState("")
  const [goal, setGoal]       = useState("")
  const [submitting, setSub]  = useState(false)
  const [err, setErr]         = useState<string | null>(null)

  const close = () => { onClose(); setName(""); setPersona(""); setGoal(""); setErr(null) }
  const valid = name.trim() && persona.trim() && goal.trim()

  async function submit() {
    if (!valid) return
    setSub(true); setErr(null)
    try {
      const csrf = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      const res = await fetch(`/skills/${skillId}/test_scenarios`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrf },
        body: JSON.stringify({ name, persona, goal }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setErr((data?.errors && data.errors[0]) || `Failed (${res.status})`)
        return
      }
      // Inertia partial reload — the parent already subscribes to a refresh
      // of the `scenarios` prop on test_run cable events; reuse that path.
      router.reload({ only: ["scenarios"] })
      close()
    } finally {
      setSub(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} size="md">
      <DialogHeader title="Add scenario" subtitle="Manual create — Forge can also draft a whole suite." />
      <DialogBody>
        <label className="block text-sm">
          <span className="mb-1 block text-app-muted">Name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="VIP user asks for cashout status" />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-app-muted">Persona</span>
          <Textarea
            rows={3}
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            placeholder="Long-time VIP user, polite, asks for plain-English status updates."
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-app-muted">Goal</span>
          <Textarea
            rows={3}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Find out why their cashout is pending and what to do next."
          />
        </label>
        {err && <p className="text-xs text-status-error">{err}</p>}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={close} disabled={submitting}>Cancel</Button>
        <Button type="button" variant="red" onClick={submit} disabled={!valid || submitting}>
          {submitting ? "Adding…" : "Add scenario"}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

function matchesFilter(status: TestRunStatus | null, runId: number | null, filter: Filter): boolean {
  switch (filter) {
    case "all":       return true
    case "passing":   return status === "passed"
    case "failing":   return status === "failed"
    case "errored":   return status === "errored"
    case "never_run": return runId == null
    case "skipped":   return status === "skipped"
    case "review":    return status === "errored" || status === "skipped"
  }
}

function visiblePageNumbers(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, index) => index + 1)
  if (currentPage <= 2) return [1, 2, 3]
  if (currentPage >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages]
  return [currentPage - 1, currentPage, currentPage + 1]
}
